import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'

import {
	ArrowRightOnRectangleIcon,
	Cog6ToothIcon,
	UsersIcon,
	ArrowTrendingUpIcon,
	ChatBubbleLeftEllipsisIcon,
	ChartBarIcon,
	ViewColumnsIcon,
	Bars3Icon,
} from '@heroicons/react/24/outline'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'

import logoImg from '../media/icons/logo-brand.svg'
import { getCurrentUser } from '../services/authService'
import {
	useSidebar,
	SIDEBAR_COLLAPSED_WIDTH,
	SIDEBAR_EXPANDED_WIDTH,
} from '../context/SidebarContext'
import { useDarkMode } from '../context/DarkModeContext'
import { clearSession } from '../services/sessionCookie'

function classNames(...classes) {
	return classes.filter(Boolean).join(' ')
}

const Tooltip = ({ children }) => (
	<span
		className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-gray-900 opacity-0 shadow-lg ring-1 ring-white/10 transition-opacity duration-150 group-hover/menu-tooltip:opacity-100"
	>
		{children}
	</span>
)

const Sidebar = () => {
	const location = useLocation()
	const user = getCurrentUser()
	const { isExpanded, isDrawerMode, toggleSidebar, expandSidebar, collapseSidebar } =
		useSidebar()
	const { isDarkMode } = useDarkMode()

	const handleContactUs = e => {
		e.preventDefault()
		if (window.$crisp) {
			window.$crisp.push(['do', 'chat:show'])
			window.$crisp.push(['do', 'chat:open'])
		}
	}

	const handleOpenBoard = e => {
		e.preventDefault()
		// The TMS is same-origin under /control, so it reads the session straight
		// out of localStorage. Handing it a copy in the URL put a credential in the
		// address bar and in history for no benefit.
		window.location.href = `${window.location.origin}/control/`
	}

	const navigation = [
		{
			name: 'Analytics',
			href: '/analytics',
			icon: ArrowTrendingUpIcon,
			current: location.pathname === '/analytics',
		},
		{
			name: 'Users',
			href: '/admin/users',
			icon: UsersIcon,
			current: location.pathname === '/admin/users',
			visible: user?.isAdmin,
		},
		{
			name: 'Load Statistics',
			href: '/loadboard',
			icon: ChartBarIcon,
			current: location.pathname === '/loadboard',
		},
		{
			name: 'Board',
			href: '#',
			icon: ViewColumnsIcon,
			current: false,
			isBoard: true,
			visible: user?.userType === 'test',
		},
		{
			name: 'Settings',
			href: '/settings',
			icon: Cog6ToothIcon,
			current: location.pathname === '/settings',
		},
		{
			name: 'Contact Us',
			href: '#',
			icon: ChatBubbleLeftEllipsisIcon,
			current: false,
			isContactUs: true,
		},
	].filter(item => item.visible !== false)

	const handleLogout = async () => {
		try {
			await axios.post('/api/user/logout-session')
		} catch (error) {
			console.log('error', error)
		} finally {
			clearSession()
			localStorage.removeItem('email')
			window.location.href = '/'
		}
	}

	const width = isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH

	// This button is placed against the document, while the install banner sits
	// above the app in normal flow, so it has to start below the banner or it
	// lands on top of it. 0 when there is no banner.
	const [bannerBottom, setBannerBottom] = useState(0)

	useEffect(() => {
		const measure = () => {
			const banner = document.getElementById('app-install-banner')
			setBannerBottom(
				banner ? banner.getBoundingClientRect().bottom + window.scrollY : 0
			)
		}

		measure()
		window.addEventListener('lf:install-banner', measure)
		window.addEventListener('resize', measure)
		return () => {
			window.removeEventListener('lf:install-banner', measure)
			window.removeEventListener('resize', measure)
		}
	}, [])

	const iconButton = isDarkMode
		? 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-100'
		: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
	const activeItem = isDarkMode
		? 'bg-white/10 text-white'
		: 'bg-gray-100 text-gray-900 font-medium'
	const inactiveItem = isDarkMode
		? 'text-gray-400 hover:bg-white/[0.05] hover:text-gray-200'
		: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'

	return (
		<>
			{/* Drawer mode: hamburger opens the off-canvas drawer. Hidden once the drawer is open. */}
			{isDrawerMode && !isExpanded && (
				<button
					type="button"
					onClick={expandSidebar}
					style={{ top: bannerBottom + 12 }}
					className={classNames(
						'absolute left-3 z-40 flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm',
						isDarkMode
							? 'bg-[#1c2127] text-gray-200 border-white/[0.08] hover:bg-white/[0.06]'
							: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
					)}
					aria-label="Open menu"
				>
					<Bars3Icon className="h-6 w-6" />
				</button>
			)}

			{/* Drawer mode: tap-to-close backdrop behind the open drawer. */}
			{isDrawerMode && isExpanded && (
				<div
					className="fixed inset-0 z-20 bg-black/40"
					onClick={collapseSidebar}
					aria-hidden="true"
				/>
			)}

			<aside
				style={{
					backgroundColor: isDarkMode ? '#1c2127' : '#ffffff',
					width: `${width}px`,
					transition: 'width 200ms ease-in-out, transform 200ms ease-in-out',
				}}
				className={classNames(
					'group fixed left-0 top-0 z-30 flex h-screen flex-col overflow-visible border-r',
					isDrawerMode
						? isExpanded
							? 'translate-x-0'
							: '-translate-x-full'
						: 'translate-x-0',
					isDarkMode
						? 'text-gray-300 border-white/[0.08]'
						: 'text-gray-700 border-gray-200'
				)}
				aria-label="Primary navigation"
			>
			{isExpanded ? (
				<div className="flex h-16 shrink-0 items-center justify-between px-4">
					<div className="flex min-w-0 items-center gap-2">
						<img src={logoImg} alt="" className="h-6 w-6 shrink-0" />
						<span className="truncate text-base font-semibold">
							<span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
								Load
							</span>
							<span style={{ color: '#2463eb' }}>Fetcher</span>
						</span>
					</div>
					<button
						type="button"
						onClick={toggleSidebar}
						className={classNames(
							'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
							iconButton
						)}
						aria-label="Collapse sidebar"
						title="Collapse sidebar"
					>
						<PanelLeftClose className="h-4 w-4" />
					</button>
				</div>
			) : (
				<div className="flex shrink-0 flex-col items-center gap-2 pt-4 pb-2">
					<img src={logoImg} alt="LoadFetcher" className="h-9 w-9" />
					<button
						type="button"
						onClick={toggleSidebar}
						className={classNames(
							'group/menu-tooltip relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
							iconButton
						)}
						aria-label="Expand sidebar"
					>
						<PanelLeftOpen className="h-4 w-4" />
						<Tooltip>Expand menu</Tooltip>
					</button>
				</div>
			)}

			<nav className={classNames('flex-grow', isExpanded ? 'px-3 pt-4' : 'px-2 pt-4')}>
				<ul className="space-y-2">
					{navigation.map(item => (
						<li key={item.name}>
							<a
								href={item.href}
								target={item.target || '_self'}
								onClick={
									item.isContactUs
										? handleContactUs
										: item.isBoard
										? handleOpenBoard
										: undefined
								}
								className={classNames(
									'group/menu-tooltip relative flex items-center rounded-xl text-[15px] transition-colors',
									isExpanded
										? 'h-11 px-3'
										: 'h-10 w-10 mx-auto justify-center px-0',
									item.current ? activeItem : inactiveItem
								)}
								aria-label={!isExpanded ? item.name : undefined}
							>
								<item.icon
									className={classNames(
										'h-5 w-5 shrink-0',
										isExpanded ? 'mr-3' : ''
									)}
								/>
								{isExpanded && (
									<span className="truncate">{item.name}</span>
								)}
								{!isExpanded && <Tooltip>{item.name}</Tooltip>}
							</a>
						</li>
					))}
				</ul>
			</nav>

			<div
				className={classNames(
					'border-t',
					isDrawerMode ? 'hidden' : 'block',
					isDarkMode ? 'border-white/[0.06]' : 'border-gray-200',
					isExpanded ? 'px-3 pb-5 pt-3' : 'px-2 pb-5 pt-3'
				)}
			>
				<button
					type="button"
					onClick={handleLogout}
					className={classNames(
						'group/menu-tooltip relative flex h-11 w-full items-center rounded-xl text-[13px] transition-colors',
						isExpanded ? 'px-3' : 'justify-center px-0',
						inactiveItem
					)}
					aria-label={!isExpanded ? 'Logout' : undefined}
				>
					<ArrowRightOnRectangleIcon
						className={classNames(
							'h-[18px] w-[18px] shrink-0',
							isExpanded ? 'mr-3' : ''
						)}
					/>
					{isExpanded && <span className="truncate">Logout</span>}
					{!isExpanded && <Tooltip>Logout</Tooltip>}
				</button>
			</div>
		</aside>
		</>
	)
}

export default Sidebar
